import { isEmptyArray, isNull, isNullable } from '@app/utils'

import { isNotNullable } from '@app/typeGuards'
import { arrayIncludesOneOf, arrayIncludesSubset, arrayUniqueValues } from '@app/utils/arrays'

import { ROLE } from '@app/constants/permission'

import { getPermissionsByRole, getDefaultPermittedRouteByRole, hasHelper } from './utils'

import type { CheckAccessOptions } from './types'
import type { Role, Permission, PermittedRoute } from '@app/types'

export class PermissionService {
  getPermissions (userRoles: Role[] | null): Permission[] | null {
    if (isNull(userRoles)) return null

    const permissions = userRoles.reduce<Permission[]>((accumulator, role) => {
      const rolePermissions = getPermissionsByRole(role)

      if (isNullable(rolePermissions)) return accumulator

      return [...accumulator, ...rolePermissions]
    }, [])

    if (isEmptyArray(permissions)) return null

    return arrayUniqueValues(permissions)
  }

  getDefaultPermittedRoute (userRoles: Role[] | null): PermittedRoute | null {
    if (isNull(userRoles)) return null

    for (const role of userRoles) {
      const defaultPermittedRoute = getDefaultPermittedRouteByRole(role)

      if (isNotNullable(defaultPermittedRoute)) return defaultPermittedRoute
    }

    return null
  }

  hasOneOfRoles (userRoles: Role[], requiredRoles: Role[]): boolean {
    return hasHelper({
      superset: userRoles,
      subset: requiredRoles,
      checkFn: arrayIncludesOneOf
    })
  }

  hasRoles (userRoles: Role[], requiredRoles: Role[]): boolean {
    return hasHelper({
      superset: userRoles,
      subset: requiredRoles,
      checkFn: arrayIncludesSubset
    })
  }

  hasOneOfPermissions (userRoles: Role[], requiredPermissions: Permission[]): boolean {
    return hasHelper({
      superset: this.getPermissions(userRoles),
      subset: requiredPermissions,
      checkFn: arrayIncludesOneOf
    })
  }

  hasPermissions (userRoles: Role[], requiredPermissions: Permission[]): boolean {
    return hasHelper({
      superset: this.getPermissions(userRoles),
      subset: requiredPermissions,
      checkFn: arrayIncludesSubset
    })
  }

  checkAccess ({
    userRoles,
    requiredRoles: rawRequiredRoles = [],
    requiredPermissions = []
  }: CheckAccessOptions): boolean {
    if (isNullable(userRoles)) return false

    const requiredRoles = [...rawRequiredRoles, ROLE.serviceAccount, ROLE.administrator]

    const hasRole = this.hasOneOfRoles(userRoles, requiredRoles)
    const hasPermissions = !isEmptyArray(requiredPermissions) && this.hasPermissions(userRoles, requiredPermissions)

    return hasRole || hasPermissions
  }
}

const permissionService = new PermissionService()

export default permissionService
